import React from 'react'
import { useList } from '@refinedev/core'
import type { Tables } from '@muaa/shared'
import { brand, money } from '../../theme/brand'
import { ListPage, SectionCard, ProgressBar, EmptyHint, type Stat } from '../../theme/listkit'
import { Trophy, HandHeart, UsersRound } from '../../components/icons'

type ClassStanding = Tables<'class_standings'>
// The admin summary view's `id` column is nullable in the generated types, which
// conflicts with refine's BaseRecord; declare just the deck columns we read.
type ClassStandingsSummary = {
  classes_ranked: number | null
  total_raised: number | null
  total_donors: number | null
}

const fmtMoney = (value: number | null) =>
  value != null ? `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'

const fmtDonors = (value: number | null) =>
  value != null ? `${value} donor${value === 1 ? '' : 's'}` : '—'

const compact = (n: number) => (n >= 1000 ? '$' + (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'K' : money(Math.round(n)))

/** Rank disc — gold for the champion, jaguar for the podium, neutral after. */
function RankDisc({ rank }: { rank: number }) {
  const gold = rank === 1
  const podium = rank <= 3
  return (
    <span
      style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: brand.fDisplay,
        fontWeight: 800,
        fontSize: 15,
        color: gold ? brand.navy900 : podium ? brand.paper : brand.sub,
        background: gold ? brand.goldGrad : podium ? brand.jaguar : brand.paper,
        border: '1px solid ' + (gold ? brand.gold : podium ? brand.jaguar : brand.cardLine),
      }}
    >
      {rank}
    </span>
  )
}

export function ClassStandingsList() {
  // The leaderboard renders one row per honor-year class (bounded by domain ~30).
  // Use an explicit pageSize cap instead of mode:'off' so the fetch is bounded.
  const { result } = useList<ClassStanding>({
    resource: 'class_standings',
    pagination: { pageSize: 200 },
    sorters: [{ field: 'raised', order: 'desc' }],
    meta: { idColumnName: 'class_year' },
  })

  const rows = React.useMemo(
    () => [...(result?.data ?? [])].sort((a, b) => Number(b.raised || 0) - Number(a.raised || 0)),
    [result],
  )
  const maxRaised = Math.max(1, ...rows.map((r) => Number(r.raised || 0)))

  // Stat deck reads ONE pre-aggregated row from the admin summary view instead
  // of reducing the full leaderboard on the main thread.
  const { result: summaryRes } = useList<ClassStandingsSummary>({
    resource: 'admin_class_standings_summary',
    pagination: { pageSize: 1 },
  })
  const summary = summaryRes?.data?.[0]
  const totalRaised = Number(summary?.total_raised ?? 0)
  const classesRanked = Number(summary?.classes_ranked ?? rows.length)
  const totalDonors = Number(summary?.total_donors ?? 0)

  const stats: Stat[] = [
    { label: 'Total Raised', value: compact(totalRaised), icon: HandHeart, tone: 'gold' },
    { label: 'Classes Ranked', value: classesRanked.toLocaleString(), icon: Trophy, tone: 'jaguar' },
    { label: 'Donors', value: totalDonors.toLocaleString(), icon: UsersRound, tone: 'green' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Giving · Class Standings',
        description:
          'Honor-year standings ranked by total raised. The classes who give most lead the board — a friendly rivalry that fuels the Annual Fund.',
      }}
      stats={stats}
      statColumns={3}
    >
      <SectionCard title="Class Giving Leaderboard" sub="Ranked by total raised">
        {rows.length === 0 ? (
          <EmptyHint>No class standings yet.</EmptyHint>
        ) : (
          <div>
            {rows.map((r, i) => {
              const rank = i + 1
              const raised = Number(r.raised || 0)
              return (
                <div
                  key={String(r.class_year)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '14px 12px',
                    borderRadius: 12,
                    marginBottom: 4,
                    background: rank === 1 ? 'rgba(196,151,49,.06)' : 'transparent',
                    border: '1px solid ' + (rank === 1 ? 'rgba(196,151,49,.25)' : 'transparent'),
                  }}
                >
                  <RankDisc rank={rank} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 15, color: brand.ink }}>
                        {`Class of ${r.class_year}`}
                      </span>
                      <span style={{ fontFamily: brand.fDisplay, fontWeight: 800, fontSize: 16, color: brand.gold }}>
                        {fmtMoney(r.raised)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 7 }}>
                      <div style={{ flex: 1 }}>
                        <ProgressBar value={raised} max={maxRaised} height={8} />
                      </div>
                      <span
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: 600,
                          fontSize: 11.5,
                          color: brand.sub,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {fmtDonors(r.participation)}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </SectionCard>
    </ListPage>
  )
}
